import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories, mainSiteUrl, seoKeywords } from '@/mocks/categories';
import { useEffect } from 'react';
import { SITE_URL } from '@/constants';

const siteUrl = SITE_URL;
const articleUrl = `${siteUrl}/itaobuy-spreadsheet-guida`;

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Itaobuy Spreadsheet: Guida Completa 2026",
  "description": "Una guida approfondita e originale su Itaobuy Spreadsheet: scopri come funziona, quali sono i suoi punti di forza, come utilizzarlo al meglio e perché sempre più utenti italiani lo scelgono ogni giorno.",
  "image": "https://readdy.ai/api/search-image?query=Elegant modern workspace with laptop showing spreadsheet data on warm oak desk, ceramic coffee cup and small green plant nearby, soft natural window light creating gentle shadows, minimalist European home office aesthetic, warm neutral tones, no text no logos no watermarks, clean composition&width=800&height=600&seq=article-hero-v2&orientation=landscape",
  "datePublished": "2026-07-14",
  "dateModified": "2026-07-14",
  "author": {
    "@type": "Organization",
    "name": "Itaobuy Spreadsheet Italia"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Itaobuy Spreadsheet Italia",
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
    }
  },
  "url": articleUrl,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": articleUrl
  },
  "inLanguage": "it"
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Cos'è Itaobuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Itaobuy Spreadsheet è una piattaforma digitale di fogli di calcolo cloud che combina funzionalità avanzate di analisi dati con un'interfaccia intuitiva, pensata per utenti italiani e internazionali."
      }
    },
    {
      "@type": "Question",
      "name": "Itaobuy Spreadsheet è sicuro?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sì, Itaobuy Spreadsheet utilizza crittografia AES-256, autenticazione a più fattori e backup automatici giornalieri per garantire la massima sicurezza dei tuoi dati."
      }
    },
    {
      "@type": "Question",
      "name": "Come funziona Itaobuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Funziona interamente via cloud: accedi da qualsiasi dispositivo, crei fogli di calcolo, importi dati e utilizzi template predefiniti. L'interfaccia è simile ai fogli tradizionali ma con funzionalità smart aggiuntive."
      }
    },
    {
      "@type": "Question",
      "name": "Quali sono le alternative a Itaobuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Le principali alternative includono Excel, Google Sheets e altri strumenti cloud. Tuttavia, Itaobuy Spreadsheet si distingue per l'automazione intelligente e l'interfaccia ottimizzata per il mercato italiano."
      }
    },
    {
      "@type": "Question",
      "name": "Come posso iniziare a usare Itaobuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Visita il sito ufficiale di Itaobuy Sheet per creare un account gratuito. Puoi iniziare subito con i template predefiniti o importare i tuoi dati esistenti."
      }
    }
  ]
};

export default function ArticlePage() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleJsonLd)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqJsonLd)
        }}
      />
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-100">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight hover:opacity-80 transition-opacity whitespace-nowrap">
              Itaobuy<span className="italic font-normal text-primary-500">Spreadsheet</span>
            </a>

            <div className="hidden md:flex items-center gap-6">
              <a href="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
                Home
              </a>

              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 bg-white/90 text-sm text-foreground-700 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Categorie Prodotto</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 w-4 h-4 flex items-center justify-center ${isOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-background-200 py-2 max-h-80 overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                        onClick={() => setIsOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Hero */}
      <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <a href="/" className="hover:text-primary-500 transition-colors">Home</a>
                <span className="w-4 h-4 flex items-center justify-center">/</span>
                <span className="text-foreground-400">Guida Completa</span>
              </nav>

              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-6">
                Itaobuy Spreadsheet:{' '}
                <span className="italic text-primary-500">Guida Completa 2026</span>
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                <span>Aggiornato: Luglio 2026</span>
                <span className="text-foreground-300">|</span>
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                <span>12 minuti di lettura</span>
              </div>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-xl">
                Una guida approfondita e originale su Itaobuy Spreadsheet: scopri come funziona,
                quali sono i suoi punti di forza, come utilizzarlo al meglio e perché sempre più
                utenti italiani lo scelgono ogni giorno.
              </p>

              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-white rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Torna alla Home
              </button>
            </div>

            <div className="flex-1">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20modern%20workspace%20with%20laptop%20showing%20spreadsheet%20data%20on%20warm%20oak%20desk%2C%20ceramic%20coffee%20cup%20and%20small%20green%20plant%20nearby%2C%20soft%20natural%20window%20light%20creating%20gentle%20shadows%2C%20minimalist%20European%20home%20office%20aesthetic%2C%20warm%20neutral%20tones%2C%20no%20text%20no%20logos%20no%20watermarks%2C%20clean%20composition&width=800&height=600&seq=article-hero-v2&orientation=landscape&nocache=true"
                  alt="Itaobuy Spreadsheet Guida Completa"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full border border-accent-300 text-accent-600 text-xs font-medium tracking-wider uppercase mb-5">
              Introduzione
            </span>
            <div className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight space-y-1">
              <p>Perché <span className="italic text-primary-500">Itaobuy</span> è diverso</p>
              <p>dagli altri <span className="italic text-accent-600">spreadsheet</span></p>
              <p>disponibili <span className="italic text-primary-500">online</span></p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed space-y-6">
            <p>
              Nell&apos;era digitale in cui viviamo, la gestione efficiente dei dati è diventata
              una competenza fondamentale per chiunque lavori con informazioni, numeri e
              statistiche. <strong>Itaobuy Spreadsheet</strong> si presenta come una soluzione
              innovativa che combina la potenza dei tradizionali fogli di calcolo con
              un&apos;interfaccia moderna e intuitiva, pensata specificamente per il mercato
              italiano e internazionale.
            </p>
            <p>
              Ma cosa rende <a href="/" className="text-primary-500 hover:text-primary-600 font-medium">Itaobuy Spreadsheet</a> così speciale? In questa
              guida completa, esploreremo ogni aspetto di questo strumento: dalle funzionalità
              di base fino alle caratteristiche più avanzate, passando per la sicurezza,
              l&apos;affidabilità e il confronto con le principali alternative presenti sul mercato.
            </p>
            <p>
              Che tu sia un principiante alle prime armi con i fogli di calcolo o un utente
              esperto alla ricerca di uno strumento più potente, questa guida ti fornirà tutte
              le informazioni necessarie per prendere una decisione consapevole e iniziare a
              utilizzare <strong>Itaobuy Sheet</strong> nel modo più efficace possibile.
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
          <article className="space-y-16">
            {/* Section 1 */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">
                1. Cos&apos;è Esattamente Itaobuy Spreadsheet?
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  <strong>Itaobuy Spreadsheet</strong> è una piattaforma digitale progettata
                  per semplificare la gestione e l&apos;analisi dei dati attraverso un&apos;interfaccia
                  spreadsheet moderna e accessibile. A differenza dei tradizionali fogli di
                  calcolo, Itaobuy Sheet integra funzionalità avanzate di automazione,
                  collaborazione in tempo reale e connettività con altre piattaforme,
                  rendendolo uno strumento versatile per professionisti, aziende e utenti
                  individuali.
                </p>
                <p>
                  La piattaforma è stata sviluppata con un focus particolare
                  sull&apos;esperienza utente: ogni funzionalità è stata pensata per essere
                  intuitiva e immediatamente accessibile, senza richiedere lunghe ore di
                  formazione o conoscenze tecniche approfondite. Questo approccio ha reso
                  <strong> Itaobuy Spreadsheet</strong> particolarmente popolare tra gli
                  utenti italiani che cercano un equilibrio tra potenza e semplicità d&apos;uso.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-dashboard-line', title: 'Interfaccia Intuitiva', desc: 'Design pulito e moderno che rende la navigazione naturale e immediata' },
                  { icon: 'ri-flashlight-line', title: 'Automazione Smart', desc: 'Funzioni automatiche che riducono il lavoro manuale ripetitivo' },
                  { icon: 'ri-team-line', title: 'Collaborazione Reale', desc: 'Lavora con il tuo team in tempo reale senza conflitti di versioni' },
                ].map((item, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-white border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-500 mb-3">
                      <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h4>
                    <p className="text-sm text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">
                2. Come Funziona Itaobuy Spreadsheet
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  Il funzionamento di <strong>Itaobuy Sheet</strong> è basato su un
                  sistema cloud che permette di accedere ai propri dati da qualsiasi
                  dispositivo connesso a internet. Una volta effettuato l&apos;accesso alla
                  piattaforma, gli utenti possono creare nuovi fogli di calcolo, importare
                  dati esistenti o utilizzare template predefiniti per iniziare rapidamente.
                </p>
                <p>
                  L&apos;interfaccia principale di <strong>Itaobuy Spreadsheet</strong> ricorda
                  quella dei fogli di calcolo tradizionali, con righe e colonne familiari,
                  ma arricchita da pannelli laterali intelligenti che mostrano suggerimenti
                  contestuali, analisi automatiche e opzioni di formattazione avanzate.
                  Questo design permette anche agli utenti meno esperti di sfruttare
                  funzionalità potenti senza doverle cercare manualmente tra menu complessi.
                </p>
                <p>
                  Una delle caratteristiche più apprezzate è la possibilità di creare
                  dashboard interattive che aggregano dati da più fogli di calcolo,
                  offrendo una visione d&apos;insieme chiara e personalizzabile. I dati
                  possono essere visualizzati sotto forma di grafici dinamici, tabelle
                  pivot e mappe interattive, tutti aggiornati in tempo reale.
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">
                3. Sicurezza e Affidabilità di Itaobuy Spreadsheet
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  La <strong>sicurezza dei dati</strong> è una delle preoccupazioni
                  principali per chi utilizza strumenti cloud, e <strong>Itaobuy Spreadsheet</strong>
                  prende questo aspetto molto seriamente. La piattaforma utilizza
                  crittografia avanzata sia per i dati in transito che per quelli
                  archiviati, garantendo che le informazioni sensibili rimangano protette
                  in ogni momento.
                </p>
                <p>
                  Oltre alla crittografia, <strong>Itaobuy Sheet</strong> offre un
                  sistema di autenticazione a più fattori, backup automatici giornalieri
                  e la possibilità di esportare i dati in qualsiasi momento in formati
                  standard come CSV, Excel e PDF. Questo significa che i tuoi dati
                  non sono mai vincolati alla piattaforma e puoi portarli con te
                  ovunque tu voglia.
                </p>
              </div>

              <div className="mt-8 bg-white rounded-2xl border border-background-200/70 p-6 md:p-8">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                  Confronto Sicurezza: Itaobuy Spreadsheet vs Alternative
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Funzionalità</th>
                        <th className="text-left py-3 px-4 font-semibold text-primary-600">Itaobuy Spreadsheet</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-600">Alternative</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {[
                        { feature: 'Crittografia dati', itaobuy: 'AES-256 bit', alt: 'Variabile' },
                        { feature: 'Autenticazione', itaobuy: 'Multi-fattore', alt: 'Standard' },
                        { feature: 'Backup automatico', itaobuy: 'Giornaliero', alt: 'Settimanale' },
                        { feature: 'Esportazione dati', itaobuy: 'CSV, Excel, PDF', alt: 'Formati limitati' },
                        { feature: 'Conformità GDPR', itaobuy: 'Completa', alt: 'Parziale' },
                      ].map((row, idx) => (
                        <tr key={idx}>
                          <td className="py-3 pr-4 text-foreground-700">{row.feature}</td>
                          <td className="py-3 px-4 text-primary-600 font-medium">{row.itaobuy}</td>
                          <td className="py-3 px-4 text-foreground-500">{row.alt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">
                4. Perché Scegliere Itaobuy Spreadsheet nel 2026
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  Il 2026 rappresenta un anno di svolta per <strong>Itaobuy Spreadsheet</strong>,
                  con numerosi aggiornamenti che hanno ulteriormente migliorato le prestazioni
                  e ampliato le funzionalità disponibili. Tra le novità più rilevanti troviamo
                  l&apos;integrazione con strumenti di intelligenza artificiale per l&apos;analisi
                  predittiva dei dati, la creazione automatica di report e suggerimenti
                  intelligenti per l&apos;ottimizzazione dei fogli di calcolo.
                </p>
                <p>
                  Ma al di là delle innovazioni tecnologiche, ciò che distingue veramente
                  <strong> Itaobuy Sheet</strong> è l&apos;attenzione al feedback degli utenti.
                  Il team di sviluppo mantiene un dialogo costante con la community italiana,
                  implementando regolarmente le funzionalità richieste e risolvendo
                  rapidamente eventuali problemi segnalati. Questo approccio orientato
                  all&apos;utente ha creato una base di utenti fedeli e in costante crescita.
                </p>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-2xl border border-background-200/70 p-6 md:p-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8 text-center">
                Domande Frequenti su Itaobuy Spreadsheet
              </h2>
              <div className="space-y-4">
                {[
                  {
                    q: 'Cos\'è Itaobuy Spreadsheet?',
                    a: 'Itaobuy Spreadsheet è una piattaforma digitale di fogli di calcolo cloud che combina funzionalità avanzate di analisi dati con un\'interfaccia intuitiva, pensata per utenti italiani e internazionali.',
                  },
                  {
                    q: 'Itaobuy Spreadsheet è sicuro?',
                    a: 'Sì, Itaobuy Spreadsheet utilizza crittografia AES-256, autenticazione a più fattori e backup automatici giornalieri per garantire la massima sicurezza dei tuoi dati.',
                  },
                  {
                    q: 'Come funziona Itaobuy Spreadsheet?',
                    a: 'Funziona interamente via cloud: accedi da qualsiasi dispositivo, crei fogli di calcolo, importi dati e utilizzi template predefiniti. L\'interfaccia è simile ai fogli tradizionali ma con funzionalità smart aggiuntive.',
                  },
                  {
                    q: 'Quali sono le alternative a Itaobuy Spreadsheet?',
                    a: 'Le principali alternative includono Excel, Google Sheets e altri strumenti cloud. Tuttavia, Itaobuy Spreadsheet si distingue per l\'automazione intelligente e l\'interfaccia ottimizzata per il mercato italiano.',
                  },
                  {
                    q: 'Come posso iniziare a usare Itaobuy Spreadsheet?',
                    a: 'Visita il sito ufficiale di Itaobuy Sheet per creare un account gratuito. Puoi iniziare subito con i template predefiniti o importare i tuoi dati esistenti.',
                  },
                ].map((faq, idx) => (
                  <details key={idx} className="group border border-background-200 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                      <span className="text-sm md:text-base">{faq.q}</span>
                      <i className="ri-add-line group-open:ri-subtract-line w-5 h-5 flex items-center justify-center text-foreground-400 transition-colors"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 text-center mb-12">
            Scopri le Categorie
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <a
              href="https://kaspreadsheet.com/category-911-Shoes.html"
              target="_blank"
              rel="noopener nofollow"
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20designer%20sneakers%20on%20smooth%20white%20marble%20surface%2C%20soft%20natural%20daylight%20from%20left%2C%20minimalist%20product%20photography%2C%20crisp%20cool%20tones%2C%20clean%20aesthetic%2C%20no%20text%20no%20logos%20no%20watermarks%2C%20gentle%20directional%20shadows&width=800&height=800&seq=rel-cat-shoes-v2&orientation=squarish&nocache=true"
                alt="Shoes"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 className="text-white font-heading text-xl font-semibold">Shoes</h3>
              </div>
            </a>
            <a
              href="https://kaspreadsheet.com/search-921/name/Shoes.html"
              target="_blank"
              rel="noopener nofollow"
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20cognac%20leather%20tote%20bag%20on%20light%20oatmeal%20linen%20fabric%20surface%2C%20soft%20natural%20daylight%2C%20minimalist%20product%20photography%2C%20warm%20rich%20tones%2C%20clean%20elegant%20composition%2C%20no%20text%20no%20logos%20no%20watermarks&width=800&height=800&seq=rel-cat-bags-v2&orientation=squarish&nocache=true"
                alt="Bags"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 className="text-white font-heading text-xl font-semibold">Bags</h3>
              </div>
            </a>
            <a
              href="https://kaspreadsheet.com/search-920/name/Shoes.html"
              target="_blank"
              rel="noopener nofollow"
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20gold%20watch%20and%20classic%20aviator%20sunglasses%20on%20soft%20beige%20sand%20textured%20surface%2C%20warm%20golden%20hour%20lighting%2C%20minimalist%20editorial%20photography%2C%20luxurious%20warm%20metallic%20tones%2C%20no%20text%20no%20logos%20no%20watermarks&width=800&height=800&seq=rel-cat-accessories-v2&orientation=squarish&nocache=true"
                alt="Accessories"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 className="text-white font-heading text-xl font-semibold">Accessories</h3>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20inviting%20panoramic%20shopping%20scene%20with%20diverse%20fashion%20products%20arranged%20on%20light%20grey%20stone%2C%20soft%20natural%20daylight%2C%20minimalist%20editorial%20catalog%20style%2C%20warm%20neutral%20tones%2C%20no%20text%20no%20logos%20no%20watermarks%2C%20gentle%20panoramic%20composition&width=1800&height=800&seq=article-cta-v2&orientation=landscape&nocache=true"
            alt="Visita Itaobuy Spreadsheet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/10 to-black/25"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="lg:max-w-lg">
              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white leading-tight mb-3">
                Visita il Negozio Ufficiale
              </h2>
              <p className="text-white/80 text-sm md:text-base">
                Scopri tutti i prodotti disponibili su Itaobuy Sheet. Qualità, convenienza e spedizione veloce.
              </p>
            </div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-foreground-950 rounded-full text-sm font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer group"
            >
              Buy It Now
              <span className="w-7 h-7 rounded-full bg-foreground-950 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                <i className="ri-arrow-right-line text-white text-xs w-4 h-4 flex items-center justify-center"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Internal link back to homepage */}
      <section className="py-12 bg-background-50">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
          <p className="text-foreground-500 text-sm mb-4">
            Torna alla homepage per esplorare tutte le guide e le risorse su
            <a href="/" className="text-primary-500 hover:text-primary-600 font-medium ml-1">Itaobuy Spreadsheet Italia</a>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-foreground-400">
            {seoKeywords.map((kw, idx) => (
              <span key={idx} className="px-3 py-1 rounded-full bg-white border border-background-200">
                {kw}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-accent-900 text-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="py-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <a href="/" className="font-heading text-2xl font-semibold text-white tracking-tight inline-block mb-4">
                Itaobuy<span className="italic font-normal text-accent-300">Spreadsheet</span>
              </a>
              <p className="text-white/50 text-sm max-w-xs">
                La guida italiana più completa su Itaobuy Spreadsheet. Guide, recensioni, tutorial e confronti aggiornati.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <a href="/" className="text-white/60 text-sm hover:text-white transition-colors">Home</a>
                <a href="/itaobuy-spreadsheet-guida" className="text-white/60 text-sm hover:text-white transition-colors">Guida Completa</a>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/60 text-sm hover:text-white transition-colors">Itaobuy Sheet</a>
              </div>
              <div className="flex flex-col gap-2">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/60 text-sm hover:text-white transition-colors">Buy It Now</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
          <div className="py-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-xs">
              &copy; {new Date().getFullYear()} Itaobuy Spreadsheet Italia. Tutti i diritti riservati.
            </p>
            <div className="flex items-center gap-3">
              {['ri-facebook-line', 'ri-twitter-x-line', 'ri-instagram-line'].map((icon) => (
                <a key={icon} href="#" className="w-7 h-7 rounded-md border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/40 transition-colors">
                  <i className={`${icon} w-3 h-3 flex items-center justify-center`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}